import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { HouseholdPermissionService } from '@se-po/shared-data-access-services'
import { DEFAULT_PERSONA_REQUIRED_FIELDS, PERSONA_REQUIRED_FIELDS, PersonaRequiredField } from '@se-po/shared-ui-persona-choice'
import { loadSeFeTheme } from 'se-fe-themes'
import { SeFeTranslationsLoader } from 'se-fe-translations'
import {
  CentralServiceResources as cs
} from 'se-resource-types'

export type PersonaPickerMinorRequiredProfileField = 'address' | 'phone' | 'graduation_year'
export type PersonaPickerAdultRequiredProfileField = 'address' | 'phone'
export type PersonaPickerRequiredProfileFields = {
  minor?: PersonaPickerMinorRequiredProfileField[]
  adult?: PersonaPickerAdultRequiredProfileField[]
}

export type PersonaPickerRequiredFields = {
  minor?: PersonaRequiredField[]
  adult?: PersonaRequiredField[]
}

@Component({
  selector: 'se-po-persona-picker',
  templateUrl: './persona-picker.component.html',
  styleUrls: ['./persona-picker.component.scss'],
  standalone: false,
})
export class PersonaPickerComponent implements OnInit {
  @Output() public pick = new EventEmitter<cs.Households.UserProfile>()
  @Output() public edit = new EventEmitter<cs.Households.UserProfile>()
  @Output() public action = new EventEmitter<{ profile: cs.Households.UserProfile; action: any }>()
  @Output() public editMissingInfo = new EventEmitter<cs.Households.UserProfile>()

  @Input() public profilesFrames: cs.Households.UserProfileFrame[]
  @Input() public set requiredFields(value: PersonaPickerRequiredFields | null | undefined) {
    this._requiredFields = value ?? undefined
    this.validateRequiredFields(this._requiredFields)
  }
  @Input() public set requiredProfileFields(value: PersonaPickerRequiredProfileFields | null | undefined) {
    this._requiredProfileFields = value ?? undefined
    this.validateRequiredProfileFields(this._requiredProfileFields)
  }

  public profileFrame: cs.Households.UserProfileFrame
  public selfProfile: cs.Households.UserProfile
  public managedProfiles: cs.Households.UserProfile[]
  public permissions: HouseholdPermissionService
  public ready = false

  private _requiredFields?: PersonaPickerRequiredFields
  private _requiredProfileFields?: PersonaPickerRequiredProfileFields
  private readonly ADDRESS_REQUIRED_FIELDS: readonly PersonaRequiredField[] = [
    'country',
    'street_address_1',
    'city',
    'state_province',
    'postal_code',
  ]

  constructor(
    private el: ElementRef,
    private seFeTranslationsLoader: SeFeTranslationsLoader
  ) {
    loadSeFeTheme({
      shadowRoot: this.el.nativeElement.shadowRoot,
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com',
    })
  }

  @Input() public set profiles(profiles: cs.Households.UserProfile[]) {
    this.updateProfiles(profiles)
  }

  public ngOnInit(): void {
    this.initTranslations()
  }

  public onPick(e: any): void {
    this.pick.emit(e)
  }

  public onEdit(e: any): void {
    this.edit.emit(e)
  }

  public onAction(e: any): void {
    this.action.emit(e)
  }

  public onEditMissingInfo(e: any): void {
    this.editMissingInfo.emit(e)
  }

  public getProfileFrame(profile: cs.Households.UserProfile): cs.Households.UserProfileFrame {
    return this.profilesFrames?.find(frame => frame.persona_id === profile.persona.id)
  }

  public getRequiredFieldsForProfile(profile: cs.Households.UserProfile): PersonaRequiredField[] | undefined {
    // When FE provides required fields, use exactly what it provides (no auto-defaulting).
    if (this._requiredFields) {
      const isMinor = this.isMinorProfile(profile)
      return (isMinor ? this._requiredFields.minor : this._requiredFields.adult) ?? []
    }

    // Preserve legacy behavior when only token-based requiredProfileFields is used.
    if (!this._requiredProfileFields) return undefined

    const isMinor = this.isMinorProfile(profile)
    const tokens = (isMinor ? this._requiredProfileFields.minor : this._requiredProfileFields.adult) ?? []
    const additionalFields = this.mapAdditionalRequiredFields(tokens as any)
    const combined = [...DEFAULT_PERSONA_REQUIRED_FIELDS, ...additionalFields]

    return Array.from(new Set(combined))
  }

  private initTranslations(): void {
    this.seFeTranslationsLoader.load('shared/ui-persona-picker/assets/translations').then(() => {
      this.ready = true
    })
  }

  private updateProfiles(profiles: cs.Households.UserProfile[]): void {
    this.permissions = HouseholdPermissionService.forProfiles(profiles)
    this.selfProfile = this.permissions.selfProfile
    this.managedProfiles = this.permissions.managedOtherProfiles
  }

  private isMinorProfile(profile: cs.Households.UserProfile): boolean {
    const age = profile?.persona?.age
    return typeof age === 'number' && age < 18
  }

  private mapAdditionalRequiredFields(
    tokens: Array<PersonaPickerMinorRequiredProfileField | PersonaPickerAdultRequiredProfileField>
  ): PersonaRequiredField[] {
    const fields: PersonaRequiredField[] = []
    for (const token of tokens) {
      switch (token) {
        case 'address':
          fields.push(...this.ADDRESS_REQUIRED_FIELDS)
          break
        case 'phone':
          fields.push('phone')
          break
        case 'graduation_year':
          fields.push('graduation_year')
          break
      }
    }
    return fields
  }

  private validateRequiredProfileFields(value?: PersonaPickerRequiredProfileFields): void {
    if (!value) return
    this.validateFieldArray(value.minor, ['address', 'phone', 'graduation_year'], 'requiredProfileFields.minor')
    this.validateFieldArray(value.adult, ['address', 'phone'], 'requiredProfileFields.adult')
  }

  private validateRequiredFields(value?: PersonaPickerRequiredFields): void {
    if (!value) return
    this.validateFieldArray(value.minor, PERSONA_REQUIRED_FIELDS, 'requiredFields.minor')
    this.validateFieldArray(value.adult, PERSONA_REQUIRED_FIELDS, 'requiredFields.adult')
  }

  private validateFieldArray(list: unknown, allowed: readonly string[], fieldPath: string): void {
    if (list == null) return
    if (!Array.isArray(list)) {
      throw new Error(`Invalid ${fieldPath}: must be an array`)
    }
    const allowedSet = new Set(allowed)
    const invalid = list.filter(v => typeof v !== 'string' || !allowedSet.has(v))
    if (invalid.length) {
      throw new Error(`Invalid ${fieldPath}: ${invalid.join(', ')}. Allowed values: ${allowed.join(', ')}`)
    }
  }
}
